import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2, ArrowLeft, ArrowRight, CheckCircle2,
  Loader2, MapPin, Phone, Mail, Hash, AlertCircle,
} from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../context/AuthContext';
import { AuthIllustration } from './AuthIllustration';
import { formatDate } from '../../lib/formatDate';
import { isValidRegistryCode, lookupErrorKey } from './onboardingErrors';
import { onboardErrorMessage } from './onboardErrors';

const API_URL = process.env.REACT_APP_API_URL || '';

interface CompanyData {
  registryCode: string;
  name: string;
  address: string;
  postalCode: string;
  email: string | null;
  phone: string | null;
  foundingDate: string | null;
  alreadyRegistered?: boolean;
}

type Step = 'loading' | 'confirm' | 'submitting' | 'success' | 'failed';

export function OnboardingPage() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const { refreshAuth } = useAuth();

  const registryCode = searchParams.get('registryCode') || '';

  const [step, setStep] = useState<Step>('loading');
  const [company, setCompany] = useState<CompanyData | null>(null);
  const [contactEmail, setContactEmail] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!registryCode) {
      navigate('/select-role');
      return;
    }
    fetchCompany();
  }, [registryCode]);

  const fetchCompany = async () => {
    setStep('loading');
    setError('');

    // The registry search matches substrings, so a mistyped code used to come
    // back as some other real company (BUG-17). Reject the format up front.
    if (!isValidRegistryCode(registryCode)) {
      setError(t(lookupErrorKey('invalid_registry_code')));
      setStep('failed');
      return;
    }

    try {
      const res = await axios.get(`${API_URL}/api/buildings/lookup/${registryCode}`, {
        withCredentials: true,
      });
      if (res.data.success) {
        if (res.data.data.alreadyRegistered) {
          setError(t('onboarding.building.alreadyRegistered'));
          setStep('failed');
          return;
        }
        setCompany(res.data.data);
        setContactEmail(res.data.data.email || '');
        setStep('confirm');
      }
    } catch (err: any) {
      // The code decides the message; the API's own sentence is English.
      setError(t(lookupErrorKey(err.response?.data?.code)));
      setStep('failed');
    }
  };

  const handleSubmit = async () => {
    if (!company) return;
    setStep('submitting');
    setError('');

    try {
      const res = await axios.post(
        `${API_URL}/api/buildings/onboard`,
        {
          registryCode: company.registryCode,
          name: company.name,
          address: company.address,
          phone: company.phone,
          email: contactEmail || company.email,
        },
        { withCredentials: true }
      );

      if (res.data.success) {
        setStep('success');
        // Refresh auth and redirect after short delay
        setTimeout(async () => {
          await refreshAuth();
          navigate('/dashboard', { replace: true });
        }, 2000);
      }
    } catch (err: any) {
      setError(onboardErrorMessage(err, t));
      setStep('confirm');
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left side */}
      <div className="flex-1 bg-card px-4 sm:px-6 py-5 flex flex-col overflow-x-hidden">
        {/* Logo */}
        <Link to="/" className="w-fit group flex items-center gap-2">
          <img src="/logo.png" alt="Unikort" className="h-9 w-auto" />
          <span className="text-xl font-semibold text-[#5756d6] dark:text-[#777aff]">
            Unikort
          </span>
        </Link>

        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-[480px]">

            {/* Loading */}
            {step === 'loading' && (
              <div className="flex flex-col items-center py-16 gap-4">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
                <p className="text-muted-foreground text-sm">{t('onboarding.building.loading')}</p>
              </div>
            )}

            {/* Error */}
            {error && step !== 'submitting' && (
              <div className="bg-destructive/10 text-destructive text-sm p-4 rounded-xl border border-destructive/20 mb-4 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            {/* Failed lookup — the banner above says why */}
            {step === 'failed' && (
              <Button
                variant="ghost"
                size="lg"
                className="w-full"
                onClick={() => navigate('/select-role')}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t('onboarding.back')}
              </Button>
            )}

            {/* Confirm step */}
            {step === 'confirm' && company && (
              <>
                <div className="mb-6">
                  <div className="inline-flex items-center gap-2 text-xs text-muted-foreground bg-muted px-3 py-1.5 rounded-full mb-4">
                    <Hash className="w-3 h-3" />
                    {t('onboarding.building.autoLoadedBadge')}
                  </div>
                  <h1 className="text-2xl sm:text-3xl font-semibold text-foreground tracking-tight">
                    {t('onboarding.building.title')}
                  </h1>
                  <p className="text-sm text-muted-foreground mt-2">
                    {t('onboarding.building.subtitle')}
                  </p>
                </div>

                {/* Auto-filled fields from Inforegister */}
                <div className="space-y-4 mb-6">
                  <div className="p-4 rounded-xl border border-border bg-muted/30 space-y-3">
                    <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
                      {t('onboarding.building.autoLoadedSection')}
                    </div>

                    <div className="flex items-start gap-3">
                      <Building2 className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-muted-foreground">{t('onboarding.building.name')}</div>
                        <div className="text-sm font-medium text-foreground">{company.name}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <Hash className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-muted-foreground">{t('onboarding.building.registryCode')}</div>
                        <div className="text-sm font-medium text-foreground font-mono">{company.registryCode}</div>
                      </div>
                    </div>

                    <div className="flex items-start gap-3">
                      <MapPin className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div>
                        <div className="text-xs text-muted-foreground">{t('onboarding.building.address')}</div>
                        <div className="text-sm font-medium text-foreground">
                          {company.address}{company.postalCode ? `, ${company.postalCode}` : ''}
                        </div>
                      </div>
                    </div>

                    {company.phone && (
                      <div className="flex items-start gap-3">
                        <Phone className="w-4 h-4 text-muted-foreground mt-0.5 flex-shrink-0" />
                        <div>
                          <div className="text-xs text-muted-foreground">{t('onboarding.building.phone')}</div>
                          <div className="text-sm font-medium text-foreground">{company.phone}</div>
                        </div>
                      </div>
                    )}

                    {company.foundingDate && (
                      <div className="flex items-start gap-3">
                        <div className="w-4 h-4 flex-shrink-0 mt-0.5" />
                        <div>
                          <div className="text-xs text-muted-foreground">{t('onboarding.building.founded')}</div>
                          <div className="text-sm font-medium text-foreground">
                            {/* An unparseable registry value is shown as it came, never as «Invalid Date». */}
                            {formatDate(company.foundingDate, i18n.language) || company.foundingDate}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Contact email — editable */}
                  <div className="space-y-2">
                    <Label htmlFor="contact-email" className="text-sm font-medium text-foreground">
                      {t('onboarding.building.emailLabel')}
                      <span className="ml-1 text-muted-foreground font-normal text-xs">
                        {t('onboarding.building.emailScope')}
                      </span>
                    </Label>
                    <div className="relative">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        id="contact-email"
                        type="email"
                        value={contactEmail}
                        onChange={(e) => setContactEmail(e.target.value)}
                        placeholder="info@korteriuhistu.ee"
                        className="pl-9"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col gap-3">
                  <Button
                    onClick={handleSubmit}
                    size="lg"
                    className="w-full"
                    disabled={!company}
                  >
                    {t('onboarding.building.submit')}
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="lg"
                    className="w-full"
                    onClick={() => navigate('/select-role')}
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('onboarding.back')}
                  </Button>
                </div>
              </>
            )}

            {/* Submitting */}
            {step === 'submitting' && (
              <div className="flex flex-col items-center py-16 gap-4">
                <Loader2 className="w-10 h-10 text-primary animate-spin" />
                <p className="text-muted-foreground text-sm">{t('onboarding.building.submitting')}</p>
              </div>
            )}

            {/* Success */}
            {step === 'success' && (
              <div className="flex flex-col items-center py-16 gap-6 text-center">
                <div className="w-20 h-20 rounded-full bg-emerald-500/10 flex items-center justify-center">
                  <CheckCircle2 className="w-10 h-10 text-emerald-500" />
                </div>
                <div>
                  <h2 className="text-2xl font-semibold text-foreground">
                    {t('onboarding.building.successTitle')}
                  </h2>
                  <p className="text-muted-foreground mt-2 text-sm">
                    {t('onboarding.building.successBody', { name: company?.name ?? '' })}
                    <br />
                    {t('onboarding.building.redirecting')}
                  </p>
                </div>
              </div>
            )}

          </div>
        </div>
      </div>

      {/* Right side */}
      <div className="hidden lg:flex flex-1 bg-gradient-to-br from-primary to-primary-hover items-center justify-center">
        <AuthIllustration />
      </div>
    </div>
  );
}
